import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData } from '../../../apiService';
import { useSessionValidation } from '../../../utils/sessionValidation';
import {
    Refresh, Print, GetApp, Search as SearchIcon
} from '@mui/icons-material';
import {
    Button, Tooltip, Alert, Snackbar, Box, Typography, CircularProgress,
    Fade, TextField, FormControl, Select, MenuItem,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import './LedgerReport.css';
import SaleModal from '../Sale/SaleModal';
import RentInvoiceModal from '../Delivery/RentInvoiceModal';

const LedgerReport = () => {
    // Session validation
    useSessionValidation();

    const { companyId, branchId, token, username, companyName } = useAuth();

    // Date range states
    const formatDateToYYYYMMDD = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getDefaultFromDate = () => {
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth(); // 0-indexed (0 = January, 3 = April)
        
        // Financial year starts on April 1st
        // If current month is January-March (0-2), use previous year's April 1st
        // If current month is April-December (3-11), use current year's April 1st
        const financialYearStart = currentMonth < 3 
            ? new Date(currentYear - 1, 3, 1) // April 1st of previous year
            : new Date(currentYear, 3, 1);     // April 1st of current year
        
        return formatDateToYYYYMMDD(financialYearStart);
    };
    const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

    const [fromDate, setFromDate] = useState(getDefaultFromDate());
    const [toDate, setToDate] = useState(getCurrentDate());

    // Data states
    const [ledgerData, setLedgerData] = useState([]);
    const [masterTypes, setMasterTypes] = useState([]);
    const [masters, setMasters] = useState([]);
    const [filteredMasters, setFilteredMasters] = useState([]);
    const [selectedMasterType, setSelectedMasterType] = useState('');
    const [selectedMaster, setSelectedMaster] = useState('');

    // Modal states
    const [openSaleModal, setOpenSaleModal] = useState(false);
    const [openRentInvoiceModal, setOpenRentInvoiceModal] = useState(false);
    const [selectedRecordId, setSelectedRecordId] = useState(null);
    const [rentInvoiceData, setRentInvoiceData] = useState(null);
    
    // Modal data states (required for SaleModal)
    const [customers, setCustomers] = useState([]);
    const [items, setItems] = useState([]);
    const [banks, setBanks] = useState([]);
    const [saleFormData, setSaleFormData] = useState({});
    const [saleDetails, setSaleDetails] = useState([]);

    // Loading and error states
    const [loading, setLoading] = useState(false);
    const [loadingMasters, setLoadingMasters] = useState(false); // Separate loading state for masters dropdown
    const [loadingLedger, setLoadingLedger] = useState(false); // Separate loading state for ledger data
    const [modalLoading, setModalLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('Loading...');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        if (companyId && token) {
            fetchMasterTypes();
        }
    }, [companyId, token]);

    useEffect(() => {
        if (selectedMasterType) {
            // Fetch masters based on selected master type
            fetchMastersByType(selectedMasterType);
            setSelectedMaster(''); // Reset selected master when master type changes
        } else {
            setFilteredMasters([]);
            setSelectedMaster('');
        }
    }, [selectedMasterType]);

    // Removed auto-fetch on dropdown change to prevent page blink
    // User must click "Get Ledger" button to fetch data

    // Fetch functions
    const fetchMasterTypes = async () => {
        try {
            setLoading(true);
            setLoadingMessage('Loading master types...');
            const response = await fetchData('MasterTypeApi', `/MasterTypeList/${companyId}`, token);
            setMasterTypes(response.response_data || response || []);
        } catch (error) {
            console.error('Error fetching master types:', error);

            // For demo purposes, create sample master types if API is not available
            const sampleMasterTypes = [
                { Id: 1, Name: 'Vendor' },
                { Id: 2, Name: 'Client' },
                { Id: 3, Name: 'Bank' },
                { Id: 4, Name: 'Account Head' }
            ];

            setMasterTypes(sampleMasterTypes);
        } finally {
            setLoading(false);
        }
    };

    const fetchMasters = async () => {
        try {
            setLoading(true);
            setLoadingMessage('Loading masters...');
            const response = await fetchData('ClientAndVendorApi', `/ClientList/${companyId}`, token);
            setMasters(response.response_data || response || []);
        } catch (error) {
            console.error('Error fetching masters:', error);

            // For demo purposes, create sample masters if API is not available
            const sampleMasters = [
                { MasterId: 1, Name: 'ABC Suppliers Pvt Ltd', MasterTypeId: 2 },
                { MasterId: 2, Name: 'XYZ Trading Company', MasterTypeId: 2 },
                { MasterId: 3, Name: 'Federal Bank', MasterTypeId: 3 },
                { MasterId: 4, Name: 'State Bank Of India', MasterTypeId: 3 },
                { MasterId: 5, Name: 'Cash Account', MasterTypeId: 4 },
                { MasterId: 6, Name: 'Sales Account', MasterTypeId: 4 },
                { MasterId: 7, Name: 'Purchase Account', MasterTypeId: 4 },
                { MasterId: 8, Name: 'John Doe', MasterTypeId: 1 },
                { MasterId: 9, Name: 'Jane Smith', MasterTypeId: 1 }
            ];

            setMasters(sampleMasters);
            setSuccess('Demo masters loaded (API not available)');
        } finally {
            setLoading(false);
        }
    };

    const fetchMastersByType = async (masterTypeId) => {
        try {
            setLoadingMasters(true); // Use separate loading state to avoid page blink
            setLoadingMessage('Loading masters...');

            let apiEndpoint = '';
            let apiController = '';

            // Determine which API to call based on Master Type
            const masterType = masterTypes.find(type => type.Id === parseInt(masterTypeId));
            const masterTypeName = masterType?.Name?.toLowerCase() || '';

            console.log('Selected Master Type:', masterTypeName, 'ID:', masterTypeId);

            if (masterTypeName === 'client') {
                apiController = 'CustomerApi';
                apiEndpoint = `/${companyId}`;
            } else if (masterTypeName === 'vendor') {
                apiController = 'VendorApi';
                apiEndpoint = `/${companyId}`;
            } else if (masterTypeName === 'bank') {
                apiController = 'BankApi';
                apiEndpoint = `/${companyId}`;
            } else if (masterTypeName === 'account head') {
                apiController = 'AccountHeadApi';
                apiEndpoint = `/${companyId}`;
            } else {
                // Default fallback
                apiController = 'CustomerApi';
                apiEndpoint = `/${companyId}`;
            }

            console.log('Fetching from:', apiController, apiEndpoint);

            const response = await fetchData(apiController, apiEndpoint, token);
            console.log('API Response:', response);

            const mastersList = response.response_data || response || [];
            console.log('Masters List:', mastersList);

            // Map the response to a consistent format
            // Handle different possible field names from API
            const formattedMasters = mastersList.map(master => ({
                MasterId: master.Id || master.MasterId || master.id,
                Name: master.Name || master.name || master.ClientName || master.VendorName || master.BankName || master.AccountHeadName,
                MasterTypeId: parseInt(masterTypeId)
            }));

            console.log('Formatted Masters:', formattedMasters);

            setFilteredMasters(formattedMasters);

            if (formattedMasters.length === 0) {
                setSuccess('No masters found for selected type');
            }

        } catch (error) {
            console.error('Error fetching masters by type:', error);

            // For demo purposes, create sample data based on master type
            const masterType = masterTypes.find(type => type.Id === parseInt(masterTypeId));
            const masterTypeName = masterType?.Name?.toLowerCase() || '';

            let sampleMasters = [];

            if (masterTypeName === 'client') {
                sampleMasters = [
                    { MasterId: 8, Name: 'John Doe', MasterTypeId: parseInt(masterTypeId) },
                    { MasterId: 9, Name: 'Jane Smith', MasterTypeId: parseInt(masterTypeId) },
                    { MasterId: 10, Name: 'Robert Johnson', MasterTypeId: parseInt(masterTypeId) },
                    { MasterId: 11, Name: 'Emily Davis', MasterTypeId: parseInt(masterTypeId) }
                ];
            } else if (masterTypeName === 'vendor') {
                sampleMasters = [
                    { MasterId: 1, Name: 'ABC Suppliers Pvt Ltd', MasterTypeId: parseInt(masterTypeId) },
                    { MasterId: 2, Name: 'XYZ Trading Company', MasterTypeId: parseInt(masterTypeId) },
                    { MasterId: 12, Name: 'Global Vendors Inc', MasterTypeId: parseInt(masterTypeId) }
                ];
            } else if (masterTypeName === 'bank') {
                sampleMasters = [
                    { MasterId: 3, Name: 'Federal Bank', MasterTypeId: parseInt(masterTypeId) },
                    { MasterId: 4, Name: 'State Bank Of India', MasterTypeId: parseInt(masterTypeId) },
                    { MasterId: 13, Name: 'HDFC Bank', MasterTypeId: parseInt(masterTypeId) },
                    { MasterId: 14, Name: 'ICICI Bank', MasterTypeId: parseInt(masterTypeId) }
                ];
            } else if (masterTypeName === 'account head') {
                sampleMasters = [
                    { MasterId: 5, Name: 'Cash Account', MasterTypeId: parseInt(masterTypeId) },
                    { MasterId: 6, Name: 'Sales Account', MasterTypeId: parseInt(masterTypeId) },
                    { MasterId: 7, Name: 'Purchase Account', MasterTypeId: parseInt(masterTypeId) },
                    { MasterId: 15, Name: 'Expense Account', MasterTypeId: parseInt(masterTypeId) }
                ];
            }

            setFilteredMasters(sampleMasters);
            setSuccess('Demo data loaded (API not available)');
        } finally {
            setLoadingMasters(false); // Use separate loading state
        }
    };

    const fetchLedgerData = async () => {
        if (!selectedMaster) return;

        try {
            setLoadingLedger(true); // Use separate loading state to avoid page blink
            setLoadingMessage('Loading ledger data...');

            // Fetch ledger data from API
            const response = await fetchData('ReportApi', `/LedgerDetails/${fromDate}/${toDate}/${companyId}/${selectedMaster}/${selectedMasterType}`, token);
            const apiData = response.response_data || response || [];

            // Filter out duplicate opening balance entries - keep only the first one
            const filteredData = apiData.filter((item, index, array) => {
                const description = (item.description1 || '').toLowerCase();
                const isOpeningBalance = description.includes('opening balance');
                
                if (isOpeningBalance) {
                    // Find the first occurrence of opening balance
                    const firstOpeningBalanceIndex = array.findIndex(
                        entry => (entry.description1 || '').toLowerCase().includes('opening balance')
                    );
                    // Keep only if this is the first opening balance entry
                    return index === firstOpeningBalanceIndex;
                }
                
                return true; // Keep all other entries
            });

            setLedgerData(filteredData);

        } catch (error) {
            console.error('Error fetching ledger data:', error);

            // For demo purposes, create sample data if API is not available
            const sampleData = [
                {
                    Date: fromDate,
                    Description: 'Opening Balance',
                    Debit: 0,
                    Credit: 0
                },
                {
                    Date: '2025-01-24',
                    Description: 'By Purchase Bill Number - SR/0001',
                    Debit: 0,
                    Credit: 1096.00
                },
                {
                    Date: '2025-01-24',
                    Description: 'By Purchase Bill Number - SR/0001',
                    Debit: 500.00,
                    Credit: 0
                },
                {
                    Date: '2025-01-28',
                    Description: 'To Federal BankBill Number: 1/25-26',
                    Debit: 1250.00,
                    Credit: 0
                },
                {
                    Date: '2025-01-29',
                    Description: 'To State Bank Of IndiaBill Number: 2/25-26',
                    Debit: 500.00,
                    Credit: 0
                },
                {
                    Date: toDate,
                    Description: 'Total',
                    Debit: 2250.00,
                    Credit: 1096.00
                },
                {
                    Date: toDate,
                    Description: 'Closing Balance',
                    Debit: 1154.00,
                    Credit: 0
                }
            ];

            setLedgerData(sampleData);
            setSuccess('Demo data loaded (API not available)');
        } finally {
            setLoadingLedger(false); // Use separate loading state
        }
    };

    // Event handlers
    const handleMasterTypeChange = (e) => {
        const masterTypeId = e.target.value;
        setSelectedMasterType(masterTypeId);
        setSelectedMaster(''); // Reset master selection
        setLedgerData([]); // Clear ledger data
    };

    const handleMasterChange = (e) => {
        const masterId = e.target.value;
        setSelectedMaster(masterId);

        // Clear previous ledger data immediately
        setLedgerData([]);

        if (masterId) {
            // Automatically fetch ledger data when master is selected
            fetchLedgerDataForMaster(masterId);
        }
    };

    // Helper function to fetch ledger data for a specific master
    const fetchLedgerDataForMaster = async (masterId) => {
        if (!masterId || !fromDate || !toDate) return;

        try {
            setLoadingLedger(true);
            setLoadingMessage('Loading ledger data...');

            // Fetch ledger data from API
            const response = await fetchData('ReportApi', `/LedgerDetails/${fromDate}/${toDate}/${companyId}/${masterId}/${selectedMasterType}`, token);
            const apiData = response.response_data || response || [];

            // Filter out duplicate opening balance entries - keep only the first one
            const filteredData = apiData.filter((item, index, array) => {
                const description = (item.description1 || '').toLowerCase();
                const isOpeningBalance = description.includes('opening balance');
                
                if (isOpeningBalance) {
                    // Find the first occurrence of opening balance
                    const firstOpeningBalanceIndex = array.findIndex(
                        entry => (entry.description1 || '').toLowerCase().includes('opening balance')
                    );
                    // Keep only if this is the first opening balance entry
                    return index === firstOpeningBalanceIndex;
                }
                
                return true; // Keep all other entries
            });

            setLedgerData(filteredData);

        } catch (error) {
            console.error('Error fetching ledger data:', error);
            setError('Failed to load ledger data');
        } finally {
            setLoadingLedger(false);
        }
    };

    const handleFromDateChange = (e) => {
        setFromDate(e.target.value);
    };

    const handleToDateChange = (e) => {
        setToDate(e.target.value);
    };

    const handleGetLedger = () => {
        if (selectedMaster && fromDate && toDate) {
            fetchLedgerData();
        } else {
            setError('Please select master and date range');
        }
    };

    const handleRefresh = () => {
        if (selectedMaster) {
            fetchLedgerData();
        } else {
            fetchMasters();
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const handleExport = () => {
        if (!ledgerData.length) {
            setError('No data to export');
            return;
        }

        try {
            // Get selected master name
            const selectedMasterObj = filteredMasters.find(m => m.MasterId === selectedMaster);
            const masterName = selectedMasterObj?.Name || 'Unknown';

            // Get selected master type name
            const selectedMasterTypeObj = masterTypes.find(t => t.Id === parseInt(selectedMasterType));
            const masterTypeName = selectedMasterTypeObj?.Name || 'Unknown';

            // Format current date and time
            const now = new Date();
            const currentDate = now.toLocaleDateString('en-IN');
            const currentTime = now.toLocaleTimeString('en-IN');

            // Create HTML table with header sections
            let html = `
                <html>
                <head>
                    <meta charset="utf-8">
                    <style>
                        table { border-collapse: collapse; width: 100%; font-family: Arial, sans-serif; }
                        .header-section { text-align: center; font-weight: bold; font-size: 16px; padding: 10px; }
                        .info-section { padding: 5px; font-size: 12px; }
                        .info-label { font-weight: bold; }
                        th { background-color: #667eea; color: white; padding: 8px; text-align: left; border: 1px solid #ddd; font-size: 12px; }
                        td { padding: 6px; border: 1px solid #ddd; font-size: 11px; }
                        .number { text-align: right; }
                        .total-row { font-weight: bold; background-color: #f3f4f6; }
                    </style>
                </head>
                <body>
                    <table>
                        <!-- Header Section -->
                        <tr>
                            <td colspan="6" class="header-section">LEDGER REPORT</td>
                        </tr>
                        
                        <!-- Company and Branch Info -->
                        <tr>
                            <td colspan="6" class="info-section">
                                <span class="info-label">Company:</span> ${companyName || 'N/A'} | 
                                <span class="info-label">Branch:</span> Branch ${branchId || 'N/A'}
                            </td>
                        </tr>
                        
                        <!-- Master Info -->
                        <tr>
                            <td colspan="6" class="info-section">
                                <span class="info-label">Master Type:</span> ${masterTypeName} | 
                                <span class="info-label">Master Name:</span> ${masterName}
                            </td>
                        </tr>
                        
                        <!-- Date Range and User Info -->
                        <tr>
                            <td colspan="6" class="info-section">
                                <span class="info-label">From Date:</span> ${new Date(fromDate).toLocaleDateString('en-IN')} | 
                                <span class="info-label">To Date:</span> ${new Date(toDate).toLocaleDateString('en-IN')} | 
                                <span class="info-label">Generated On:</span> ${currentDate} ${currentTime} | 
                                <span class="info-label">Created By:</span> ${username || 'N/A'}
                            </td>
                        </tr>
                        
                        <!-- Empty Row -->
                        <tr><td colspan="6" style="height: 10px;"></td></tr>
                        
                        <!-- Table Headers -->
                        <tr>
                            <th>Sl No</th>
                            <th>Date</th>
                            <th>Description</th>
                            <th>Debit (₹)</th>
                            <th>Credit (₹)</th>
                            <th>Balance (₹)</th>
                        </tr>
            `;

            // Add table rows
            let runningBalance = 0;
            ledgerData.forEach((ledger, index) => {
                const debitAmount = parseFloat(ledger.debit) || 0;
                const creditAmount = parseFloat(ledger.credit) || 0;
                const description = (ledger.description1 || '').toLowerCase();
                const isTotal = description.includes('total');
                const isClosingBalance = description.includes('closing balance');

                // Calculate running balance for all entries except "Total"
                if (!isTotal) {
                    runningBalance += creditAmount - debitAmount;
                }

                const rowClass = (isTotal || isClosingBalance) ? 'total-row' : '';
                const balanceDisplay = (isTotal || isClosingBalance) 
                    ? '-' 
                    : `${Math.abs(runningBalance).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${runningBalance < 0 ? '(Dr)' : '(Cr)'}`;

                html += `
                    <tr class="${rowClass}">
                        <td>${index + 1}</td>
                        <td>${ledger.date1 ? new Date(ledger.date1).toLocaleDateString('en-IN') : '-'}</td>
                        <td>${ledger.description1 || '-'}</td>
                        <td class="number">${debitAmount > 0 ? debitAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</td>
                        <td class="number">${creditAmount > 0 ? creditAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</td>
                        <td class="number">${balanceDisplay}</td>
                    </tr>
                `;
            });

            html += `
                    </table>
                </body>
                </html>
            `;

            // Create blob and download
            const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Ledger_Report_${masterName}_${currentDate.replace(/\//g, '-')}.xls`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            setSuccess('Ledger report exported successfully');
        } catch (error) {
            console.error('Error exporting ledger report:', error);
            setError('Failed to export ledger report');
        }
    };

    const handleCloseSnackbar = () => {
        setError('');
        setSuccess('');
    };

    // Fetch data required for modals
    const fetchModalData = async () => {
        try {
            // Fetch customers
            const customersResponse = await fetchData('CustomerApi', `/${companyId}`, token);
            setCustomers(customersResponse.response_data || customersResponse || []);

            // Fetch items
            const itemsResponse = await fetchData('ItemApi', `/${companyId}`, token);
            setItems(itemsResponse.response_data || itemsResponse || []);

            // Fetch banks
            const banksResponse = await fetchData('BankApi', `/${companyId}`, token);
            setBanks(banksResponse.response_data || banksResponse || []);
        } catch (error) {
            console.error('Error fetching modal data:', error);
        }
    };

    // Fetch sale data by ID
    const fetchSaleById = async (saleId) => {
        try {
            setModalLoading(true);
            const response = await fetchData('SaleApi', `/${saleId}`, token);
            const freshData = response.response_data || response;
            
            if (freshData) {
                const saleData = freshData.Sale || freshData;
                const details = freshData.SaleDetails || [];
                
                // Format the data to match what SaleModal expects
                setSaleFormData({
                    Id: saleData.Id,
                    CompanyId: saleData.CompanyId || companyId,
                    BranchId: saleData.BranchId || branchId,
                    CustomerId: saleData.CustomerId,
                    CustomerName: saleData.CustomerName || '',
                    ContactNumber1: saleData.ContactNumber1 || '',
                    ContactNumber2: saleData.ContactNumber2 || '',
                    AddressLine1: saleData.AddressLine1 || '',
                    AddressLine2: saleData.AddressLine2 || '',
                    Location: saleData.Location || '',
                    PinCode: saleData.PinCode || '',
                    BillNumber: saleData.BillNumber,
                    FinYear: saleData.FinYear || '',
                    BillDate: saleData.BillDate?.split('T')[0] || new Date().toISOString().split('T')[0],
                    Amount: saleData.Amount || 0,
                    DiscountAmount: saleData.DiscountAmount || 0,
                    NetAmount: saleData.NetAmount || 0,
                    CGSTAmount: saleData.CGSTAmount || 0,
                    SGSTAmount: saleData.SGSTAmount || 0,
                    IGSTAmount: saleData.IGSTAmount || 0,
                    RoundOff: saleData.RoundOff || 0,
                    TransportingCharge: saleData.TransportingCharge || 0,
                    TotalAmount: saleData.TotalAmount || 0,
                    AdvanceAmount: saleData.AdvanceAmount || 0,
                    PaidAmount: saleData.PaidAmount || 0,
                    BalanceAmount: saleData.BalanceAmount || 0,
                    ModeOfPayment: saleData.ModeOfPayment || 0,
                    Remarks: saleData.Remarks || '',
                    IsGST: saleData.IsGST ?? true,
                    IsActive: saleData.IsActive ?? true,
                    CreatedBy: saleData.CreatedBy,
                    CreatedDate: saleData.CreatedDate,
                    CreatedByName: saleData.CreatedByName,
                    ModifiedBy: saleData.ModifiedBy,
                    ModifiedDate: saleData.ModifiedDate,
                    ModifiedByName: saleData.ModifiedByName
                });
                
                setSaleDetails(details.map(d => ({
                    Id: d.Id || 0,
                    ItemId: d.ItemId,
                    HSNCode: d.HSNCode || '',
                    GSTRate: d.GSTRate || 0,
                    Quantity: d.Quantity || 0,
                    Rate: d.Rate || 0,
                    Amount: d.Amount || 0,
                    DiscountPercentage: d.DiscountPercentage || 0,
                    DiscountAmount: d.DiscountAmount || 0,
                    TaxableAmount: d.TaxableAmount || 0,
                    CGSTAmount: d.CGSTAmount || 0,
                    SGSTAmount: d.SGSTAmount || 0,
                    IGSTAmount: d.IGSTAmount || 0,
                    TotalAmount: d.TotalAmount || 0,
                    IsActive: d.IsActive ?? true,
                    CreatedBy: d.CreatedBy,
                    CreatedDate: d.CreatedDate,
                    ModifiedBy: d.ModifiedBy,
                    ModifiedDate: d.ModifiedDate
                })));
            }
        } catch (error) {
            console.error('Error fetching sale data:', error);
            setError('Failed to load sale data');
        } finally {
            setModalLoading(false);
        }
    };

    // Fetch rent invoice data by ID
    const fetchRentInvoiceById = async (rentInvoiceId) => {
        try {
            setModalLoading(true);
            const response = await fetchData('RentInvoiceApi', `/${rentInvoiceId}`, token);
            const freshData = response.response_data || response;
            
            if (freshData) {
                // The API should return { RentInvoice: {...}, RentInvoiceDetails: [...] }
                setRentInvoiceData(freshData);
            }
        } catch (error) {
            console.error('Error fetching rent invoice data:', error);
            setError('Failed to load rent invoice data');
        } finally {
            setModalLoading(false);
        }
    };

    const handleDescriptionClick = async (ledger) => {
        // Skip if it's Opening Balance, Total, or Closing Balance
        const description = (ledger.description1 || '').toLowerCase();
        if (description.includes('opening balance') || 
            description.includes('total') || 
            description.includes('closing balance')) {
            return;
        }

        const tableName = ledger.tbl1;
        const recordId = ledger.id;

        if (!tableName || !recordId) return;

        // Fetch modal data if not already loaded
        if (customers.length === 0 || items.length === 0 || banks.length === 0) {
            await fetchModalData();
        }

        // Open appropriate modal based on table name
        switch (tableName.toLowerCase()) {
            case 'sale':
                await fetchSaleById(recordId);
                setSelectedRecordId(recordId);
                setOpenSaleModal(true);
                break;
            case 'rentinvoice':
                await fetchRentInvoiceById(recordId);
                setSelectedRecordId(recordId);
                setOpenRentInvoiceModal(true);
                break;
            // Add more cases as needed for other transaction types
            default:
                console.log('No modal configured for table:', tableName);
                break;
        }
    };

    const handleCloseSaleModal = () => {
        setOpenSaleModal(false);
        setSelectedRecordId(null);
        setSaleFormData({});
        setSaleDetails([]);
    };

    const handleCloseRentInvoiceModal = () => {
        setOpenRentInvoiceModal(false);
        setSelectedRecordId(null);
        setRentInvoiceData(null);
    };

    const handleSaveSale = () => {
        // This is view mode only, no save needed
        handleCloseSaleModal();
    };

    if (!mounted) return null;

    return (
        <Fade in={mounted} timeout={800}>
            <div style={{
                minWidth: '100%',
                background: '#f5f7fa',
                padding: '10px',
                position: 'relative'
            }}>
                {/* Loading Overlay */}
                {loading && (
                    <Box
                        sx={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            zIndex: 9999,
                            backdropFilter: 'blur(4px)'
                        }}
                    >
                        <Box sx={{ textAlign: 'center' }}>
                            <CircularProgress
                                size={60}
                                thickness={4}
                                sx={{
                                    color: '#667eea',
                                    mb: 2
                                }}
                            />
                            <Typography
                                variant="h6"
                                sx={{
                                    color: '#667eea',
                                    fontWeight: 600,
                                    fontSize: '1rem'
                                }}
                            >
                                {loadingMessage}
                            </Typography>
                        </Box>
                    </Box>
                )}

                <style>{`
          @keyframes slideIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .action-button {
            text-transform: none;
            font-weight: 600;
            letter-spacing: 0.5px;
          }
        `}</style>

                <div style={{
                    maxWidth: '1400px',
                    margin: '0 auto',
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    padding: '12px',
                    position: 'relative',
                    color: 'black',
                    fontFamily: 'Times New Romans',
                }}>
                    {/* Header Section */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '10px',
                        paddingBottom: '8px',
                        borderBottom: '1px solid #f0f0f0'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div>
                                <h4 style={{
                                    fontSize: '17px',
                                    fontWeight: '700',
                                    margin: 0,
                                    letterSpacing: '-0.3px',
                                    fontFamily: 'Times New Romans',
                                    textTransform: 'uppercase'
                                }}>
                                    <span style={{ color: 'black', fontFamily: 'Times New Romans', }}>Ledger Report</span>
                                </h4>
                            </div>
                        </div>
                    </div>

                    {/* Filters Row */}
                    <div style={{
                        marginBottom: '12px',
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        WebkitOverflowScrolling: 'touch',
                        scrollbarWidth: 'thin'
                    }}>
                        <div style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'flex-end',
                            minWidth: 'fit-content',
                            paddingBottom: '4px'
                        }}>
                            {/* From Date */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
                                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>From Date</label>
                                <TextField
                                    type="date"
                                    size="small"
                                    value={fromDate}
                                    onChange={handleFromDateChange}
                                    sx={{
                                        width: isMobile ? '120px' : '140px',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            backgroundColor: '#f8f9fa',
                                            fontSize: '11px',
                                            height: '32px',
                                            '&:hover fieldset': { borderColor: '#667eea' },
                                            '&.Mui-focused fieldset': { borderColor: '#667eea' },
                                        },
                                    }}
                                />
                            </div>

                            {/* To Date */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
                                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>To Date</label>
                                <TextField
                                    type="date"
                                    size="small"
                                    value={toDate}
                                    onChange={handleToDateChange}
                                    sx={{
                                        width: isMobile ? '120px' : '140px',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            backgroundColor: '#f8f9fa',
                                            fontSize: '11px',
                                            height: '32px',
                                            '&:hover fieldset': { borderColor: '#667eea' },
                                            '&.Mui-focused fieldset': { borderColor: '#667eea' },
                                        },
                                    }}
                                />
                            </div>

                            {/* Master Type Dropdown */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '150px' : '200px' }}>
                                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>Master Type</label>
                                <FormControl size="small" sx={{ minWidth: isMobile ? '150px' : '200px' }}>
                                    <Select
                                        value={selectedMasterType}
                                        onChange={handleMasterTypeChange}
                                        displayEmpty
                                        sx={{
                                            borderRadius: '6px',
                                            backgroundColor: '#f8f9fa',
                                            fontSize: '11px',
                                            height: '32px',
                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
                                        }}
                                    >
                                        <MenuItem value="" sx={{ fontSize: '11px' }}>Select Master Type</MenuItem>
                                        {masterTypes.map(type => (
                                            <MenuItem key={type.Id} value={type.Id} sx={{ fontSize: '11px' }}>
                                                {type.Name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>

                            {/* Master Dropdown */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '180px' : '250px' }}>
                                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>Master</label>
                                <FormControl size="small" sx={{ minWidth: isMobile ? '180px' : '250px' }}>
                                    <Select
                                        value={selectedMaster}
                                        onChange={handleMasterChange}
                                        displayEmpty
                                        disabled={!selectedMasterType}
                                        sx={{
                                            borderRadius: '6px',
                                            backgroundColor: selectedMasterType ? '#f8f9fa' : '#e5e7eb',
                                            fontSize: '11px',
                                            height: '32px',
                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: selectedMasterType ? '#667eea' : '#d1d5db' },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
                                        }}
                                        renderValue={(selected) => {
                                            if (!selected || selected === '') {
                                                return <span style={{ color: '#9ca3af', fontSize: '11px' }}>
                                                    {selectedMasterType ? 'Select Master' : 'Select Master Type First'}
                                                </span>;
                                            }
                                            const selectedMasterObj = filteredMasters.find(m => m.MasterId === selected);
                                            return selectedMasterObj?.Name || 'Select Master';
                                        }}
                                    >
                                        <MenuItem value="" sx={{ fontSize: '11px', display: 'none' }}>
                                            {selectedMasterType ? 'Select Master' : 'Select Master Type First'}
                                        </MenuItem>
                                        {filteredMasters.map(master => (
                                            <MenuItem key={master.MasterId} value={master.MasterId} sx={{ fontSize: '11px' }}>
                                                {master.Name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>

                            {/* Get Ledger Button */}
                            <Tooltip title="Get ledger data" arrow>
                                <Button
                                    variant="contained"
                                    onClick={handleGetLedger}
                                    disabled={!selectedMaster || loadingLedger}
                                    className="action-button"
                                    style={{
                                        background: selectedMaster && !loadingLedger ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#e5e7eb',
                                        color: selectedMaster && !loadingLedger ? 'white' : '#9ca3af',
                                        padding: isMobile ? '0' : '4px 12px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '700',
                                        boxShadow: selectedMaster && !loadingLedger ? '0 2px 8px rgba(102, 126, 234, 0.4)' : 'none',
                                        border: 'none',
                                        height: '32px',
                                        textTransform: 'none',
                                        letterSpacing: '0.5px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '4px',
                                        whiteSpace: 'nowrap',
                                        minWidth: isMobile ? '32px' : '100px',
                                        width: 'auto'
                                    }}
                                >
                                    {loadingLedger ? (
                                        <CircularProgress size={18} sx={{ color: '#9ca3af' }} />
                                    ) : (
                                        <SearchIcon style={{ fontSize: '18px' }} />
                                    )}
                                    {!isMobile && <span style={{ fontSize: '12px' }}>{loadingLedger ? 'Loading...' : 'Get Ledger'}</span>}
                                </Button>
                            </Tooltip>

                            {/* Export Button */}
                            <Tooltip title="Export report" arrow>
                                <Button
                                    variant="contained"
                                    onClick={handleExport}
                                    disabled={!ledgerData.length}
                                    className="action-button"
                                    style={{
                                        background: ledgerData.length ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : '#e5e7eb',
                                        color: ledgerData.length ? 'white' : '#9ca3af',
                                        padding: isMobile ? '0' : '4px 12px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '700',
                                        boxShadow: ledgerData.length ? '0 2px 8px rgba(245, 158, 11, 0.4)' : 'none',
                                        border: 'none',
                                        height: '32px',
                                        textTransform: 'none',
                                        letterSpacing: '0.5px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '4px',
                                        whiteSpace: 'nowrap',
                                        minWidth: isMobile ? '32px' : 'auto',
                                        width: 'auto'
                                    }}
                                >
                                    <GetApp style={{ fontSize: '18px' }} />
                                    {!isMobile && <span style={{ fontSize: '12px' }}>Export</span>}
                                </Button>
                            </Tooltip>
                        </div>
                    </div>

                    {/* Table */}
                    <div style={{ marginBottom: '8px' }}>
                        <TableContainer component={Paper} style={{
                            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
                            borderRadius: '6px',
                            overflow: 'hidden'
                        }}>
                            <Table sx={{ minWidth: 650 }} size="small" aria-label="ledger report table">
                                <TableHead>
                                    <TableRow style={{
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        height: '24px'
                                    }}>
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '9px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.2px',
                                            padding: '2px 6px',
                                            textAlign: 'center',
                                            height: '24px',
                                            width: '5%'
                                        }}>
                                            Sl No
                                        </TableCell>
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '9px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.2px',
                                            padding: '2px 6px',
                                            textAlign: 'left',
                                            height: '24px',
                                            width: '10%'
                                        }}>
                                            Date
                                        </TableCell>
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '9px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.2px',
                                            padding: '2px 6px',
                                            textAlign: 'left',
                                            height: '24px',
                                            width: '35%'
                                        }}>
                                            Description
                                        </TableCell>
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '9px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.2px',
                                            padding: '2px 6px',
                                            textAlign: 'right',
                                            height: '24px',
                                            width: '13%'
                                        }}>
                                            Debit
                                        </TableCell>
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '9px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.2px',
                                            padding: '2px 6px',
                                            textAlign: 'right',
                                            height: '24px',
                                            width: '13%'
                                        }}>
                                            Credit
                                        </TableCell>
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '9px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.2px',
                                            padding: '2px 6px',
                                            textAlign: 'right',
                                            height: '24px',
                                            width: '14%'
                                        }}>
                                            Balance
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(() => {
                                        let runningBalance = 0;
                                        return ledgerData.map((ledger, index) => {
                                            const debitAmount = parseFloat(ledger.debit) || 0;
                                            const creditAmount = parseFloat(ledger.credit) || 0;

                                            // Check for special entries
                                            const description = (ledger.description1 || '').toLowerCase();
                                            const isTotal = description.includes('total');
                                            const isClosingBalance = description.includes('closing balance');

                                            // Calculate running balance for all entries except "Total"
                                            if (!isTotal) {
                                                runningBalance += creditAmount - debitAmount;
                                            }

                                            return (
                                                <TableRow
                                                    key={ledger.id || index}
                                                    sx={{
                                                        '&:nth-of-type(even)': { backgroundColor: '#fafbfc' },
                                                        '&:hover': {
                                                            backgroundColor: '#f1f5f9',
                                                            transition: 'all 0.15s ease-in-out'
                                                        },
                                                        borderBottom: '1px solid #f1f5f9',
                                                        height: '28px'
                                                    }}
                                                >
                                                    <TableCell sx={{ py: 0.5, px: 0.75, textAlign: 'center' }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#6b7280', fontSize: '0.7rem' }}>
                                                            {index + 1}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ py: 0.5, px: 0.75 }}>
                                                        <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 500, color: '#374151' }}>
                                                            {ledger.date1 ? new Date(ledger.date1).toLocaleDateString('en-IN') : '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell 
                                                        sx={{ 
                                                            py: 0.5, 
                                                            px: 0.75,
                                                            cursor: ledger.tbl1 && ledger.id && 
                                                                   !['az', 'zy', 'zz'].includes((ledger.tbl1 || '').toLowerCase()) 
                                                                   ? 'pointer' : 'default'
                                                        }}
                                                        onClick={() => handleDescriptionClick(ledger)}
                                                    >
                                                        <Typography 
                                                            variant="body2" 
                                                            sx={{ 
                                                                fontSize: '0.7rem', 
                                                                color: ledger.tbl1 && ledger.id && 
                                                                       !['az', 'zy', 'zz'].includes((ledger.tbl1 || '').toLowerCase())
                                                                       ? '#667eea' : '#374151',
                                                                fontWeight: ledger.tbl1 && ledger.id && 
                                                                           !['az', 'zy', 'zz'].includes((ledger.tbl1 || '').toLowerCase())
                                                                           ? 500 : 400,
                                                                '&:hover': ledger.tbl1 && ledger.id && 
                                                                          !['az', 'zy', 'zz'].includes((ledger.tbl1 || '').toLowerCase())
                                                                          ? {
                                                                              textDecoration: 'underline',
                                                                              color: '#764ba2'
                                                                          } : {}
                                                            }}
                                                        >
                                                            {ledger.description1 || '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ py: 0.5, px: 0.75, textAlign: 'right' }}>
                                                        <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#dc2626' }}>
                                                            {debitAmount > 0 ? `₹${debitAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ py: 0.5, px: 0.75, textAlign: 'right' }}>
                                                        <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#059669' }}>
                                                            {creditAmount > 0 ? `₹${creditAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{
                                                        py: 0.5,
                                                        px: 0.75,
                                                        textAlign: 'right',
                                                        backgroundColor: (isTotal || isClosingBalance) ? 'transparent' : (runningBalance >= 0 ? '#f0fdf4' : '#fef2f2')
                                                    }}>
                                                        <Typography variant="body2" sx={{
                                                            fontSize: '0.7rem',
                                                            fontWeight: 700,
                                                            color: (isTotal || isClosingBalance) ? '#6b7280' : (runningBalance >= 0 ? '#059669' : '#dc2626')
                                                        }}>
                                                            {(isTotal || isClosingBalance) ? '-' : (
                                                                `₹${Math.abs(runningBalance).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${runningBalance < 0 ? ' (Dr)' : ' (Cr)'}`
                                                            )}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        });
                                    })()}
                                    {ledgerData.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3 }}>
                                                <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.7rem' }}>
                                                    {selectedMaster ? 'No ledger entries found for the selected date range' : 'Select master and date range to view ledger entries'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Closing Balance Summary */}
                        {ledgerData.length > 0 && (
                            <Box sx={{
                                mt: 1,
                                p: 1.5,
                                background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                                borderRadius: '6px',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                gap: 1.5
                            }}>
                                <Typography variant="body1" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                                    Closing Balance:
                                </Typography>
                                <Box sx={{
                                    px: 2,
                                    py: 0.75,
                                    borderRadius: '4px',
                                    backgroundColor: (() => {
                                        const closingBalanceEntry = ledgerData.find(ledger =>
                                            (ledger.description1 || '').toLowerCase().includes('closing balance')
                                        );
                                        if (closingBalanceEntry) {
                                            const debitAmount = parseFloat(closingBalanceEntry.debit) || 0;
                                            const creditAmount = parseFloat(closingBalanceEntry.credit) || 0;
                                            const balance = creditAmount - debitAmount;
                                            return balance >= 0 ? '#059669' : '#dc2626';
                                        }
                                        return '#6b7280';
                                    })()
                                }}>
                                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#fff', fontSize: '0.85rem' }}>
                                        {(() => {
                                            const closingBalanceEntry = ledgerData.find(ledger =>
                                                (ledger.description1 || '').toLowerCase().includes('closing balance')
                                            );
                                            if (closingBalanceEntry) {
                                                const debitAmount = parseFloat(closingBalanceEntry.debit) || 0;
                                                const creditAmount = parseFloat(closingBalanceEntry.credit) || 0;
                                                const balance = creditAmount - debitAmount;
                                                return `₹${Math.abs(balance).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${balance < 0 ? '(Dr)' : '(Cr)'}`;
                                            }
                                            return 'No closing balance found';
                                        })()}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </div>
                </div>

                {/* Success/Error Snackbar */}
                <Snackbar
                    open={!!success}
                    autoHideDuration={3000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                        {success}
                    </Alert>
                </Snackbar>

                <Snackbar
                    open={!!error}
                    autoHideDuration={3000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                        {error}
                    </Alert>
                </Snackbar>

                {/* Sale Modal */}
                {openSaleModal && (
                    <SaleModal
                        open={openSaleModal}
                        onClose={handleCloseSaleModal}
                        modalAction="view"
                        formData={saleFormData}
                        setFormData={setSaleFormData}
                        saleDetails={saleDetails}
                        setSaleDetails={setSaleDetails}
                        onSave={handleSaveSale}
                        customers={customers}
                        items={items}
                        banks={banks}
                        loading={modalLoading}
                        companyId={companyId}
                        branchId={branchId}
                        token={token}
                        onPrintInvoice={() => {}}
                    />
                )}

                {/* Rent Invoice Modal */}
                {openRentInvoiceModal && rentInvoiceData && (
                    <RentInvoiceModal
                        open={openRentInvoiceModal}
                        onClose={handleCloseRentInvoiceModal}
                        deliveryData={rentInvoiceData}
                        mode="view"
                        onSave={null}
                    />
                )}

                {/* Modal Loading Overlay */}
                {modalLoading && (
                    <Box
                        sx={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(15, 23, 42, 0.75)',
                            zIndex: 10000,
                            backdropFilter: 'blur(8px)'
                        }}
                    >
                        <Box sx={{ 
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2
                        }}>
                            {/* Animated Spinner */}
                            <Box
                                sx={{
                                    width: '80px',
                                    height: '80px',
                                    border: '4px solid rgba(102, 126, 234, 0.2)',
                                    borderTop: '4px solid #667eea',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite',
                                    '@keyframes spin': {
                                        '0%': { transform: 'rotate(0deg)' },
                                        '100%': { transform: 'rotate(360deg)' }
                                    }
                                }}
                            />
                            <Typography
                                variant="h6"
                                sx={{
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '1.1rem',
                                    letterSpacing: '0.5px'
                                }}
                            >
                                Loading Transaction...
                            </Typography>
                        </Box>
                    </Box>
                )}
            </div>
        </Fade>
    );
};

export default LedgerReport;
